import { useState } from "react";
import { Search, ShoppingCart, User, MapPin, Menu, X, LogOut, SlidersHorizontal, Info, Sparkles, Crown, Heart, ChevronLeft, PhoneCall, Lock } from "lucide-react";
import { City, User as UserType, MembershipPlan } from "../types";
import { DEFAULT_RENTLOW_NAP } from "../utils/seoOptimizationEngine";

interface HeaderProps {
  cities: City[];
  selectedCity: string;
  onCityChange: (cityId: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  cartCount: number;
  onOpenCart: () => void;
  wishlistCount?: number;
  onOpenWishlist?: () => void;
  currentUser: UserType | null;
  onOpenLogin: () => void;
  onLogout: () => void;
  currentTab: string;
  onTabChange: (tab: string) => void;
  onOpenMembership?: () => void;
  plans?: MembershipPlan[];
  googleBusinessUrl?: string;
  onGoHome?: () => void;
  supportPhone?: string;
}

export default function Header({
  cities,
  selectedCity,
  onCityChange,
  searchQuery,
  onSearchChange,
  cartCount,
  onOpenCart,
  wishlistCount = 0,
  onOpenWishlist,
  currentUser,
  onOpenLogin,
  onLogout,
  currentTab,
  onTabChange,
  onOpenMembership,
  plans,
  googleBusinessUrl,
  onGoHome,
  supportPhone,
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleHomeClick = () => {
    setMobileMenuOpen(false);
    if (onGoHome) {
      onGoHome();
    } else {
      onTabChange("home");
      onSearchChange("");
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const maxDiscount = plans && plans.length > 0 ? Math.max(...plans.map(p => p.discountPercent || 10)) : 10;
  const activeCityName = cities.find(c => c.id === selectedCity)?.name || "Select City";

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs transition-all">
      {/* Top Accent Gradient Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-[#0077B6] via-[#0284C7] to-[#FF7F50]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          
          {/* Logo Section - Acts as Home Button */}
          <div className="flex items-center gap-2 sm:gap-6">
            {currentTab !== "home" && (
              <button
                onClick={handleHomeClick}
                className="flex items-center gap-1 px-2.5 py-1.5 -ml-1 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-800 text-xs font-extrabold rounded-lg border border-slate-200 cursor-pointer transition-all shrink-0"
                aria-label="Back to storefront"
                title="Back to Appliances Storefront"
                id="header-back-to-store-btn"
              >
                <ChevronLeft className="h-4 w-4 text-[#0077B6] stroke-[2.5]" />
                <span className="hidden sm:inline font-sans">Store</span>
              </button>
            )}

            <button 
              onClick={handleHomeClick} 
              className="flex flex-col items-start focus:outline-none cursor-pointer group select-none transition-transform active:scale-95 py-1 pr-1 sm:pr-2 touch-manipulation"
              aria-label="RentLow Home - Back to Storefront"
              title="RentLow Home"
              id="rentlow-header-logo-btn"
            >
              <div className="flex items-baseline font-sans text-xl sm:text-2xl font-extrabold tracking-tight">
                <span className="text-slate-900 group-hover:text-[#0077B6] transition-colors">Rent</span>
                <span className="text-[#0077B6] font-black">Low</span>
              </div>
              <span className="text-[9px] sm:text-[10px] font-extrabold tracking-widest text-[#FF7F50] font-sans -mt-1 uppercase text-left">
                रेंट से लो
              </span>
            </button>

            {/* City Selector - Always visible on mobile and desktop */}
            <div className="relative flex items-center gap-1 bg-slate-100 hover:bg-slate-200/80 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg border border-slate-200 cursor-pointer transition-colors duration-150 min-h-[44px]">
              <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#0077B6] shrink-0" />
              <select
                value={selectedCity}
                onChange={(e) => onCityChange(e.target.value)}
                aria-label="Select serviceable city"
                className="bg-transparent text-[11px] sm:text-xs font-bold text-slate-800 focus:outline-none cursor-pointer pr-0.5 sm:pr-1 uppercase tracking-wider font-sans"
              >
                {cities.map((city) => (
                  <option key={city.id} value={city.id} className="text-slate-900 bg-white font-sans">
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* Global Search Bar (Desktop) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search appliances (e.g. Fridge, TV...)"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="Search appliances by keyword"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6]/30 transition-all duration-150 shadow-2xs font-sans"
              />
              <Search className="absolute left-4 top-3 h-4.5 w-4.5 text-slate-400" />
            </div>
          </div>

          {/* Action Buttons & Navigation */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            
            {/* Google Business Profile Verified Badge Link (Visible on md+ to prevent mobile header overflow) */}
            <a
              href={googleBusinessUrl || DEFAULT_RENTLOW_NAP.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 text-slate-800 font-bold text-[11px] sm:text-xs rounded-full shadow-2xs hover:shadow-xs transition-all cursor-pointer min-h-[44px]"
              title="RentLow Official Google Business Profile & Customer Reviews"
              id="google-business-header-btn"
            >
              {/* Google SVG Logo Icon */}
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <div className="flex items-center gap-1 font-sans">
                <span className="font-extrabold text-slate-900">4.9</span>
                <span className="text-amber-500 font-black text-xs">★</span>
                <span className="hidden sm:inline text-slate-500 font-medium ml-0.5">Google</span>
              </div>
            </a>

            {/* Direct Support Call Link in Header (Visible on sm+) */}
            <a
              href={`tel:${supportPhone || "+919300691533"}`}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold text-xs rounded-full shadow-2xs hover:shadow-xs transition-all cursor-pointer min-h-[44px] min-w-[44px] justify-center"
              title={`Call RentLow Support Desk: ${supportPhone || "+919300691533"}`}
              aria-label={`Call Support Desk at ${supportPhone || "+919300691533"}`}
            >
              <PhoneCall className="h-4 w-4 text-emerald-700 shrink-0" />
              <span className="hidden sm:inline font-sans">Call</span>
            </a>

            {/* Desktop Nav Links */}
            <nav className="hidden lg:flex items-center gap-1 shrink-0">
              <button
                onClick={handleHomeClick}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 ${
                  currentTab === "home" ? "text-[#0077B6] font-extrabold border-b-2 border-[#0077B6]" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Explore
              </button>
              
              {/* Membership Plan Highlighted Button */}
              {onOpenMembership && (
                <button
                  onClick={onOpenMembership}
                  className="px-3 py-1.5 rounded-lg text-xs font-black bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white hover:from-amber-600 hover:to-amber-800 shadow-xs hover:shadow-md transition-all cursor-pointer flex items-center gap-1.5 border border-amber-400/50 whitespace-nowrap shrink-0"
                  id="header-membership-btn"
                >
                  <Crown className="h-3.5 w-3.5 text-amber-200 fill-amber-300 animate-pulse" />
                  <span>👑 MEMBERSHIP (SAVE {maxDiscount}%)</span>
                </button>
              )}

              <button
                onClick={() => onTabChange("upcoming-launchpad")}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 ${
                  currentTab === "upcoming-launchpad" ? "text-[#0077B6] font-extrabold border-b-2 border-[#0077B6]" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                🚀 Upcoming Launchpad
              </button>
              <button
                onClick={() => onTabChange("bookings")}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 ${
                  currentTab === "bookings" ? "text-[#0077B6] font-extrabold border-b-2 border-[#0077B6]" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                My Bookings
              </button>
              {currentUser?.role === "admin" ? (
                <button
                  onClick={() => onTabChange("admin")}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 ${
                    currentTab === "admin" 
                      ? "text-white bg-[#0077B6] font-extrabold shadow-sm border-[#0077B6]" 
                      : "text-[#0077B6] bg-sky-50/80 hover:bg-sky-100/80 border-sky-200"
                  }`}
                  id="header-admin-nav-btn"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  <span>Admin Panel</span>
                  <span className="bg-amber-400 text-slate-900 text-[9px] font-black px-1.5 py-0.5 rounded font-mono uppercase">LIVE</span>
                </button>
              ) : (
                <button
                  onClick={() => onTabChange("admin")}
                  className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 text-slate-500 hover:text-[#0077B6] hover:bg-slate-100 rounded-lg text-xs font-semibold transition-colors cursor-pointer shrink-0"
                  title="Admin Security Portal"
                  aria-label="Admin Security Portal Login"
                  id="header-admin-portal-login-btn"
                >
                  <Lock className="h-3.5 w-3.5" />
                  <span>Admin Portal</span>
                </button>
              )}
            </nav>

            {/* Mobile Direct Admin Quick Icon Button */}
            {currentUser?.role === "admin" ? (
              <button
                onClick={() => onTabChange("admin")}
                className={`lg:hidden flex items-center justify-center p-2 min-h-[40px] min-w-[40px] rounded-lg transition-colors cursor-pointer border ${
                  currentTab === "admin"
                    ? "bg-purple-700 text-white border-purple-800 shadow-xs"
                    : "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                }`}
                title="Admin Control Center"
                aria-label="Open Admin Control Center"
                id="mobile-header-admin-btn"
              >
                <SlidersHorizontal className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => onTabChange("admin")}
                className="lg:hidden flex items-center justify-center p-2 min-h-[40px] min-w-[40px] rounded-lg transition-colors cursor-pointer text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                title="Admin Portal Login"
                aria-label="Admin Portal Login"
                id="mobile-header-admin-login-btn"
              >
                <Lock className="h-4 w-4" />
              </button>
            )}

            {/* Wishlist Button (Visible on sm+) */}
            <button
              onClick={onOpenWishlist}
              className="hidden sm:flex relative p-2 min-h-[44px] min-w-[44px] items-center justify-center text-slate-700 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors duration-150 cursor-pointer"
              id="header-wishlist-btn"
              aria-label={`Open wishlist. ${wishlistCount} items saved.`}
              title="My Saved Wishlist"
            >
              <Heart className="h-6 w-6 text-slate-700 hover:text-rose-600 transition-colors" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-md bg-rose-500 text-[10px] font-black text-white ring-2 ring-white shadow-xs">
                  {wishlistCount}
                </span>
              )}
            </button>

            {/* Cart Button (Always visible) */}
            <button
              onClick={onOpenCart}
              className="relative p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-slate-700 hover:text-[#0077B6] hover:bg-slate-100 rounded-lg transition-colors duration-150 cursor-pointer"
              id="header-cart-btn"
              aria-label={`Open shopping cart. ${cartCount} items selected.`}
            >
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4.5 w-4.5 sm:h-5 sm:w-5 items-center justify-center rounded-md bg-[#FF7F50] text-[9px] sm:text-[10px] font-black text-white ring-2 ring-white shadow-xs">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Profile/Auth Button (Desktop - Only visible if authenticated admin) */}
            {currentUser && currentUser.role === "admin" && (
              <div className="hidden lg:flex items-center gap-2">
                <div className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200/80 border border-slate-200 rounded-lg pl-2 pr-3 py-1.5 text-xs font-semibold text-slate-800">
                  <div className="h-5.5 w-5.5 rounded-md bg-[#0077B6]/15 text-[#0077B6] flex items-center justify-center uppercase font-bold">
                    A
                  </div>
                  <span className="max-w-[120px] truncate">{currentUser.name}</span>
                </div>
                <button
                  onClick={onLogout}
                  title="Sign Out"
                  className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors duration-150 cursor-pointer"
                  aria-label="Sign out of admin panel"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            )}

            {/* Mobile Menu Button (Always visible on mobile/tablet) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:text-[#0077B6] hover:bg-slate-100 rounded-lg transition-colors duration-150 cursor-pointer min-h-[40px] min-w-[40px] flex items-center justify-center"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              id="mobile-header-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

          </div>
        </div>

        {/* Mobile Search Bar Row (Matches desktop search capability on mobile view) */}
        <div className="md:hidden pb-2.5 pt-0.5">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search appliances (e.g. Fridge, TV, AC...)"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label="Search appliances by keyword"
              className="w-full bg-slate-50 border border-slate-200/90 rounded-xl py-2 pl-9 pr-8 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6]/30 transition-all duration-150 shadow-2xs"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="absolute right-2.5 top-2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-4 pt-2 pb-6 space-y-4 shadow-lg">
          
          {/* Mobile City Selector */}
          <div className="flex sm:hidden items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
            <span className="text-xs font-medium text-slate-500">Current City</span>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-[#0077B6]" />
              <select
                value={selectedCity}
                onChange={(e) => {
                  onCityChange(e.target.value);
                  setMobileMenuOpen(false);
                }}
                aria-label="Select serviceable city"
                className="bg-transparent text-sm font-semibold text-slate-800 focus:outline-none cursor-pointer"
              >
                {cities.map((city) => (
                  <option key={city.id} value={city.id} className="bg-white text-slate-900">
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search bar on mobile */}
          <div className="relative md:hidden w-full">
            <input
              type="text"
              placeholder="Search appliances..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label="Search appliances by keyword"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0077B6] transition-all duration-150"
            />
            <Search className="absolute left-4 top-3.5 h-4 w-4 text-slate-400" />
          </div>

          {/* Navigation links */}
          <div className="grid grid-cols-1 gap-2.5">
            {/* Authenticated Admin Quick Control Card - Only visible if authenticated admin */}
            {currentUser?.role === "admin" && (
              <div className="p-3 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 rounded-xl text-white shadow-md border border-purple-500/30 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="p-2 bg-purple-500/25 rounded-lg text-purple-300 shrink-0">
                    <SlidersHorizontal className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-black tracking-tight text-white flex items-center gap-1.5">
                      <span>Admin Control Center</span>
                      <span className="bg-amber-400 text-purple-950 text-[8px] font-black px-1.5 py-0.2 rounded font-mono uppercase">
                        PORTAL
                      </span>
                    </div>
                    <p className="text-[10px] text-purple-200/80 truncate">
                      Bookings, inventory & site controls
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onTabChange("admin");
                    setMobileMenuOpen(false);
                  }}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-400 active:scale-95 text-white font-extrabold text-xs rounded-lg shadow-xs transition-all shrink-0 cursor-pointer"
                  id="drawer-admin-enter-btn"
                >
                  Open →
                </button>
              </div>
            )}

            <button
              onClick={handleHomeClick}
              className={`w-full text-left px-4 py-3 rounded-lg text-sm font-semibold cursor-pointer ${
                currentTab === "home" ? "bg-slate-100 text-[#0077B6]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              Explore Appliances
            </button>
            {onOpenMembership && (
              <button
                onClick={() => { onOpenMembership(); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-lg text-xs font-black bg-gradient-to-r from-amber-500 to-amber-700 text-white cursor-pointer flex items-center justify-between shadow-xs"
              >
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-amber-200 fill-amber-300" />
                  <span>👑 MEMBERSHIP CLUB (₹599 & ₹899/YR)</span>
                </div>
                <span className="bg-white text-amber-900 text-[9px] font-black px-2 py-0.5 rounded font-mono">10% OFF</span>
              </button>
            )}
            {onOpenWishlist && (
              <button
                onClick={() => { onOpenWishlist(); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-lg text-sm font-semibold text-rose-700 bg-rose-50/70 hover:bg-rose-100/70 border border-rose-200 cursor-pointer flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
                  <span>My Saved Wishlist</span>
                </div>
                <span className="bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full font-mono">
                  {wishlistCount}
                </span>
              </button>
            )}
            <button
              onClick={() => { onTabChange("bookings"); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg text-sm font-semibold cursor-pointer ${
                currentTab === "bookings" ? "bg-slate-100 text-[#0077B6]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              My Bookings
            </button>
            <a
              href={googleBusinessUrl || DEFAULT_RENTLOW_NAP.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full text-left px-4 py-3 rounded-lg text-sm font-extrabold bg-amber-50 text-amber-900 border border-amber-200 cursor-pointer flex items-center justify-between shadow-2xs hover:bg-amber-100"
            >
              <div className="flex items-center gap-2">
                <span className="bg-amber-500 text-white font-black rounded-full h-4.5 w-4.5 flex items-center justify-center text-[10px]">G</span>
                <span>Google Business Store (4.9 ⭐)</span>
              </div>
              <span className="text-[10px] text-amber-700 font-mono font-bold">1,850+ Reviews →</span>
            </a>

            {/* Authenticated Admin Links in Drawer */}
            {currentUser && currentUser.role === "admin" ? (
              <>
                <button
                  onClick={() => { onTabChange("admin"); setMobileMenuOpen(false); }}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm font-semibold border flex items-center justify-between cursor-pointer ${
                    currentTab === "admin" ? "bg-[#0077B6] text-white border-[#0077B6]" : "bg-sky-50/80 text-[#0077B6] hover:bg-sky-100 border-sky-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    <span>Admin Panel Dashboard</span>
                  </div>
                  <span className="bg-amber-400 text-slate-900 text-[9px] font-black px-1.5 py-0.5 rounded font-mono uppercase">LIVE</span>
                </button>
                <button
                  onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 cursor-pointer flex items-center gap-2 mt-2 border-t border-slate-200 pt-3"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout Administrator</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => { onTabChange("admin"); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 cursor-pointer flex items-center justify-between border border-dashed border-slate-300 mt-2"
                title="Admin Security Login"
                id="mobile-drawer-admin-login-btn"
              >
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-[#0077B6]" />
                  <span className="font-bold text-slate-800">Admin Security Portal</span>
                </div>
                <span className="bg-sky-100 text-[#0077B6] text-[10px] font-black px-2 py-0.5 rounded font-mono">LOGIN →</span>
              </button>
            )}
          </div>

        </div>
      )}
    </header>
  );
}
